import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Calendar, X } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Reusable period filter + universal search bar.
 * Used across Invoices, Bills, Other Payments, Other Receipts.
 */
export function PeriodSearchFilter({ searchQuery, onSearchChange, periodFilter, onPeriodChange, customDateRange, onCustomDateChange }) {
  const [showCustom, setShowCustom] = useState(false);

  const handlePeriodChange = (val) => {
    onPeriodChange(val);
    setShowCustom(val === 'custom');
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="period-search-filter">
      {/* Universal Search */}
      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Search by name, description, category or amount..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 pr-8 h-9 text-sm"
          data-testid="universal-search-input"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Period Filter */}
      <div className="flex gap-2 items-center">
        <Calendar className="w-4 h-4 text-gray-400 hidden sm:block" />
        <Select value={periodFilter} onValueChange={handlePeriodChange}>
          <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="period-filter-select">
            <SelectValue placeholder="Period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="this_quarter">This Quarter</SelectItem>
            <SelectItem value="this_year">This Year</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>

        {showCustom && (
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              value={customDateRange.start}
              onChange={(e) => onCustomDateChange({ ...customDateRange, start: e.target.value })}
              className="w-[130px] h-9 text-sm"
              data-testid="custom-date-start"
            />
            <span className="text-gray-400 text-sm">to</span>
            <Input
              type="date"
              value={customDateRange.end}
              onChange={(e) => onCustomDateChange({ ...customDateRange, end: e.target.value })}
              className="w-[130px] h-9 text-sm"
              data-testid="custom-date-end"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Helper: get date range from a period filter value.
 * Returns { start, end } as ISO date strings or null for "all".
 */
export function getDateRangeFromPeriod(period, customRange = {}) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed

  switch (period) {
    case 'this_month': {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'last_month': {
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'this_quarter': {
      const qStart = Math.floor(month / 3) * 3;
      const start = new Date(year, qStart, 1);
      const end = new Date(year, qStart + 3, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'this_year': {
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    }
    case 'custom': {
      return { start: customRange.start || null, end: customRange.end || null };
    }
    default:
      return { start: null, end: null };
  }
}

function fmt(d) {
  return d.toISOString().split('T')[0];
}

/**
 * Apply client-side filtering to an array of items.
 * Searches across multiple fields and filters by date range.
 */
export function applyFilters(items, { searchQuery, periodFilter, customDateRange, dateField = 'date', searchFields = [] }) {
  let filtered = [...items];

  // Period filter
  const range = getDateRangeFromPeriod(periodFilter, customDateRange);
  if (range.start) {
    filtered = filtered.filter(item => String(item[dateField] || item.date || '').slice(0, 10) >= range.start);
  }
  if (range.end) {
    filtered = filtered.filter(item => String(item[dateField] || item.date || '').slice(0, 10) <= range.end);
  }

  // Search filter
  if (searchQuery && searchQuery.trim()) {
    const q = searchQuery.toLowerCase().trim();
    filtered = filtered.filter(item => {
      return searchFields.some(field => {
        const val = item[field];
        if (val == null) return false;
        return String(val).toLowerCase().includes(q);
      });
    });
  }

  // Newest document date first; same-day documents fall back to posting time (created_at).
  const docDate = (item) => item[dateField] || item.date || item.created_at || '';
  const key = (item) => `${String(docDate(item)).slice(0, 10)}|${item.created_at || ''}`;
  return filtered.sort((a, b) => (key(a) < key(b) ? 1 : key(a) > key(b) ? -1 : 0));
}
